import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Background, { loadBackgroundData, BackgroundProps } from './components/background'
import LoginForm from './components/login-form'
import JoinForm from './components/join-form'
import { Helmet } from 'react-helmet'
import BackButton from 'mobile/components/user/back-button'
import ToggleButton from 'shared/components/toggle-button'
import { mobile } from 'shared/util/media-query'
import isMobile from 'shared/util/is-mobile'
import FormErrors from './components/form-errors'
import EmailValidation from 'shared/components/email-validation'
import SignupRecoveryModal, {
    SignupRecoveryModalProps,
} from '../../shared/components/signup-recovery-modal/signup-recovery-modal'
import { resendEmail } from '../../shared/api'

const CONTAINER_WIDTH = isMobile() ? 325 : 352

const Container = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
`

const Content = styled.div`
    width: ${CONTAINER_WIDTH}px;
`

const BackContainer = styled.div`
    position: absolute;
    top: 21px;
    left: 21px;
    z-index: 9999999;
`

const BeAnimated = styled.img`
    width: calc(100% - 32px);
    margin-bottom: 10px;
    margin-left: 16px;
    margin-right: 16px;

    ${mobile.css`
        width: 242px;
        margin-bottom: 10px;
    `}
`

const Left = styled.div`
    flex-direction: column;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    width: 100%;
    height: 100%;
`

const Right = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    width: 100%;
    height: 100%;

    ${mobile.css`
        flex: 0;
        display: none;
    `}
`

const Gradient = styled.div`
    position: absolute;
    height: 199px;
    width: 100%;
    background: linear-gradient(-180deg, rgba(41, 41, 41, 0) 0%, rgba(18, 18, 18, 0.8) 100%);
    border-radius: 0px;
    bottom: 0;
`

interface AuthPage {
    startInLogin?: boolean
}

const Join = ({ startInLogin = true }: AuthPage) => {
    const [backgroundProps, setBackgroundProps] = useState<BackgroundProps>()
    const [showLogin, setShowLogin] = useState(startInLogin)
    const [createdUser, setCreatedUser] = useState({ username: '', email: '' })
    const [emailConfirmed, setEmailConfirmed] = useState(false)
    const [modalProps, setModalProps] = useState<SignupRecoveryModalProps>({})

    const loadBackground = async () => {
        const { webmUrl, mp4Url, profilePath, avatarUrl, name } = await loadBackgroundData()

        setBackgroundProps({
            webmUrl,
            mp4Url,
            profilePath,
            avatarUrl,
            name,
        })
    }

    useEffect(() => {
        loadBackground()
    }, [])

    useEffect(() => {
        if (emailConfirmed) {
            setShowLogin(true)
        }
    }, [emailConfirmed])

    const startEmailValidation = (user) => {
        setCreatedUser(user)
    }

    const modalClose = () => {
        setModalProps({})
    }

    const submitModalData = async (email: string) => {
        try {
            await resendEmail({ email })

            setCreatedUser({ email, username: email })
            setModalProps({})
        } catch (error) {
            setModalProps({
                title: 'Oops!',
                text: "We didn't recognize that email. Please try reentering the email address connected to your account. Note that unconfirmed registrations are deleted after three days, so you may need to sign up again.",
                inputPlaceholder: 'Email Address',
                ctaText: 'Send',
                closeClickCallback: modalClose,
                image: 'https://media0.giphy.com/media/28DaHBc6rGZgaUSThG/giphy.gif?cid=790b76116bc788a071546d2d298fcbefa6f89145a4693151&rid=giphy.gif&ct=s',
                ctaClickCallback: submitModalData,
                imageTopOffset: -95,
            })
        }
    }

    const startSignUpRecovery = () => {
        setModalProps({
            title: 'Hello, again!',
            text: "If you registered but did not confirm your account yet, enter your email address and we'll send you another confirmation email. Be sure to check your spam folder.",
            inputPlaceholder: 'Email Address',
            ctaText: 'Send',
            closeClickCallback: modalClose,
            image: 'https://media4.giphy.com/media/rYV2FpxZ3gUizs2usC/giphy.gif?cid=790b7611245e6d582e7bfc39b52549278682c7f29817ba96&rid=giphy.gif&ct=s',
            ctaClickCallback: submitModalData,
            imageTopOffset: -55,
        })
    }

    return (
        <Container>
            {isMobile() && (
                <BackContainer>
                    <BackButton
                        onTapAction={() => {
                            if (createdUser?.email?.length) {
                                setCreatedUser({ username: '', email: '' })
                            } else {
                                history.back()
                            }
                        }}
                    />
                </BackContainer>
            )}
            <Helmet>
                <title>{showLogin ? 'Login' : 'Join'} | GIPHY</title>
            </Helmet>
            <Left>
                <Background {...backgroundProps} blur={true} withAttribution={false} autoFillBgProps={false} />
                <Content>
                    {createdUser?.email?.length && !emailConfirmed ? (
                        <>
                            <EmailValidation user={createdUser} setEmailConfirmed={setEmailConfirmed} />
                        </>
                    ) : (
                        <>
                            <BeAnimated src="/static/img/be-animated.gif" />
                            <ToggleButton
                                onText={'Log In'}
                                offText={'Sign Up'}
                                isOn={showLogin}
                                width={CONTAINER_WIDTH}
                                height={42}
                                toggle={() => setShowLogin(!showLogin)}
                            />
                            {showLogin ? (
                                <LoginForm width={CONTAINER_WIDTH} />
                            ) : (
                                <JoinForm
                                    width={CONTAINER_WIDTH}
                                    onJoinSuccess={startEmailValidation}
                                    onFinishSignUpClick={startSignUpRecovery}
                                />
                            )}
                            {modalProps?.title && <SignupRecoveryModal {...modalProps} />}
                        </>
                    )}
                </Content>
            </Left>
            <Right>
                <Gradient />
                <Background {...backgroundProps} autoFillBgProps={false} />
            </Right>
            <FormErrors />
        </Container>
    )
}

export default Join
